import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { ParentsType } from "../../../wp/getParentPages";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import Split from "@/components/_primitives/Split/Split";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { ImageType } from "@/types/baseTypes";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { getCategories } from "@/wp/getCategory";
import { useState, useEffect } from "react";
import { getVideoData } from "@/getData/getVideoData";
import { useRouter } from "next/router";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { Headline } from "@/components/Headline/Headline";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import styles from "./PostHeader.module.scss";
import { Button } from "@/components/Button/Button";
import { CustomLink } from "@/components/CustomLink";

export interface PostHeaderProps {
  post: Post<"blog">;
  headerOptions?: HeadersOptionsType["header_posts"];
  parents: ParentsType;
  fallbackImg?: ImageType | false;
}

export const PostHeader = ({
  post,
  headerOptions,
  parents,
  fallbackImg,
}: PostHeaderProps) => {
  const header = post.acf.header_post;
  const {
    // overline = "",
    // subline = "",
    media,
    type,
    // background_color_class,
    section_margin_class,
    // headline_text_align_class,
    row_direction_class,
    content,
    seo_settings: { breadcrumb_title },
    link,
  } = header;

  const {
    // show_overline_subline: show_overline_subline,
    textheader,
    imageheader,
    splitheader,
    breadcrumb_position,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  // const { height: textheaderHeight } = textheader ?? {};
  const {
    // height: imageheaderHeight,
    // media_format: imageheaderMediaFormat,
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
  } = imageheader ?? {};
  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  /* jscpd:ignore-start */
  const {
    // showOverline,
    // showSubline,
    showDate,
    showCategories,
    showDateAndCategories,
  } = useSublinesVisibility("categories");
  /* jscpd:ignore-end */

  const date = new Date(post.date_gmt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const [postCategories, setPostCategories] = useState<
    { name: string; slug: string }[]
  >([]);

  useEffect(() => {
    async function fetchCat() {
      const categories = await getCategories({ category: "categories" });
      setPostCategories(
        categories
          .filter((cat) => post?.categories?.includes(cat.id))
          .map((cat) => ({ name: cat.name, slug: cat.slug })),
      );
    }
    fetchCat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSubline = showDate || showCategories || showDateAndCategories;
  const overlineDateString = `${showDate || showDateAndCategories ? date : ""}${showDateAndCategories ? " | " : ""}`;
  const displayCategoryButtons =
    (showCategories || showDateAndCategories) && postCategories.length > 0;

  const splitHeaderDirection: "row" | "row-reverse" =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const breadcrumbsJSX = (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        // post={post}
        breadcrumbPositon={
          breadcrumb_position
            ? breadcrumb_position
            : "is-justify-content-flex-start"
        }
        breadcrumbTitle={breadcrumb_title}
      />
    </div>
  );

  const mediaJSX = (
    <div className="section py-0 w-full">
      <div className={`container ${styles["media-wrapper"]}`}>
        <div className={`column is-10-tablet is-8-fullhd mx-auto p-0`}>
          {breadcrumbsJSX}
          {media && media.type === "image" ? (
            <MediaItem
              image_desktop={
                media.image_desktop
                  ? media.image_desktop
                  : fallbackImg
                    ? fallbackImg
                    : false
              }
              image_tablet={media.image_tablet}
              image_mobile={media.image_mobile}
              image_fullhd={media.image_fullhd}
              // mediaFormat={imageheaderMediaFormat}
              sizes="(max-width: 767px) 90vw, (max-width: 1407px) 75vw, 1024w"
              priority={true}
              mediaFormat={
                header.type === "splitheader"
                  ? splitheaderMediaFormat
                  : undefined
              }
              className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
            />
          ) : media &&
            media.type === "video" &&
            videoData &&
            videoData.acf.header_videos.video_type === "upload" ? (
            <HeaderVideoPlayer
              video={videoData.acf.header_videos}
              autoplay={media.autoplay}
              singlePageUrl={videoData.link}
            />
          ) : (
            <></>
          )}
          <time className="date">{date}</time>
        </div>
      </div>
    </div>
  );
  const categoriesJSX = (
    <div className={styles["categories-wrapper"]}>
      {overlineDateString}
      {displayCategoryButtons &&
        postCategories.map((cat) => (
          <CustomLink key={cat.slug} target="_blank" href={cat.slug}>
            {cat.name.replaceAll("&amp;", "&")}
          </CustomLink>
        ))}
    </div>
  );
  const contentJSX = (
    <>
      <Headline
        headline={header.title}
        headline_html_tag="h1-posts"
        extraClass={`is-flex is-flex-direction-column has-text-centered`}
        // subline={overlineJSX}
        disableAnimation
      />

      {categoriesJSX}
      {content && <ContentTag extraClass="intro-text" content={content} />}
      {link && link.url && (
        <Button
          href={link.url}
          aria-label={link.aria_label}
          target={link.target}
          type="primary"
          className="mb-6 mx-auto"
        >
          {link.title}
        </Button>
      )}
    </>
  );

  switch (type as "text" | "imageheader" | "splitheader" | "textheader") {
    case "text":
    case "textheader":
      return (
        <HeaderBase
          className={`${section_margin_class} ${styles["post-header"]}`}
          header={
            <div
              className={`has-dunkelrot-bg p-4 is-relative mb-6 text-header`}
            >
              {breadcrumbsJSX}
              <div className="section py-0">
                <div className="container">
                  <div className="columns is-centered">
                    <div
                      className={`column ${columnSize(textheaderTextWidth ?? false)}`}
                    >
                      <Headline
                        headline={header.title}
                        headline_html_tag="h1-posts"
                        extraClass={`is-flex is-flex-direction-column has-text-centered`}
                        // subline={overlineJSX}
                        disableAnimation
                      />

                      {showSubline && categoriesJSX}
                    </div>
                  </div>
                </div>
              </div>

              <time className="date">{date}</time>
            </div>
          }
          content={
            content ? (
              <ContentTag extraClass="intro-text" content={content} />
            ) : (
              <></>
            )
          }
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${section_margin_class} columns m-0 is-multiline is-centered ${styles["post-header"]} ${styles["image-header"]}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(imageheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={imageheaderTextHorizontalAlign || "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient ?? false}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={`min-height-acceptor-desktop `}
              second={contentJSX}
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX
                  : undefined
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
